import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { NearViewMap } from "../near-view-map";
import { PW, pewterPanelApproved } from "../../internal-ui/pewter-shell";
import { DirectionsLink } from "../location-card";
import { EngravedPanel } from "./engraved";

export interface MapCardProps {
  latitude: number | string;
  longitude: number | string;
  /** The area line under the map: the city today. */
  place: string;
  className?: string;
}

/**
 * The restaurant's map: the same Google Maps embed the page always had, now
 * inside a rounded frame on the pewter panel, plus a Directions control that
 * opens turn-by-turn routing (the thing a diner actually wants from a pin).
 * The control is the same one the activity location card uses.
 */
export function MapCard({ latitude, longitude, place, className }: MapCardProps) {
  const { t } = useTranslation();

  return (
    <EngravedPanel
      aria-labelledby="map-card-title"
      className={`flex flex-col p-3 ${className ?? ""}`}
      style={pewterPanelApproved(22)}
    >
      <div className="flex items-center justify-between gap-3 px-2 pb-3 pt-2">
        <div className="min-w-0">
          <h2 id="map-card-title" className="font-heading text-[20px] font-bold md:text-[22px]" style={{ color: PW.navy }}>
            {t("activity.location")}
          </h2>
          <p className="mt-0.5 flex min-w-0 items-center gap-1.5 font-sans text-[13px]" style={{ color: PW.muted }}>
            <Icon name="ri-map-pin-line" className="shrink-0 text-[14px]" aria-hidden="true" />
            <span className="min-w-0 line-clamp-2">{place}</span>
          </p>
        </div>
        <DirectionsLink latitude={latitude} longitude={longitude} />
      </div>
      {/* The frame, not the panel, clips: the panel keeps its outer shadow. */}
      <div className="min-h-[240px] flex-1 overflow-hidden rounded-[14px]" style={{ boxShadow: `0 0 0 1px ${PW.hairline}` }}>
        <NearViewMap
          title={t("activity.map")}
          latitude={latitude}
          longitude={longitude}
          className="block h-full min-h-[240px] w-full border-0"
        />
      </div>
    </EngravedPanel>
  );
}
